import {
  IItem,
  ISupplier,
  IUnit,
  OrderTemplate,
  OrderTemplateLine,
  PurchaseOrder,
  PurchaseOrderLine,
} from '$models'
import dayjs from 'dayjs'
import { PurchaseOrderStatus } from '$constants'
import { PurchaseOrderUiLine } from '../../types'

/**
 * Replaces a purchase order line in the array with an updated version.
 *
 * Finds the line by `lineId` using `findIndex` (faster than `forEach` for early exits),
 * and returns a new array with that line replaced.
 * Returns `null` if the input array is invalid, or the original array if the line isn't found.
 *
 * @param lines - The current array of purchase order lines
 * @param updatedLine - The updated line to insert
 * @returns A new array with the line replaced, or null if input is invalid
 */
export function updatePurchaseOrderLine(
  lines: PurchaseOrderUiLine[],
  updatedLine: PurchaseOrderUiLine
): PurchaseOrderUiLine[] | null {
  if (!lines) return null

  const index = lines.findIndex((line) => line.lineId === updatedLine.lineId)
  if (index === -1) return lines

  const updated = [...lines]
  updated[index] = updatedLine

  return updated
}

export function convertLinesToPurchaseOrders(
  uiLines: PurchaseOrderUiLine[],
  orderedBy: string,
  notes?: string,
  creditRequest: boolean = false
): PurchaseOrder[] {
  const grouped = new Map<string, PurchaseOrderUiLine[]>()

  for (const line of uiLines) {
    if (!line.supplierId) continue

    if (!grouped.has(line.supplierId)) {
      grouped.set(line.supplierId, [])
    }
    grouped.get(line.supplierId)!.push(line)
  }

  const purchaseOrders: PurchaseOrder[] = []

  for (const [supplierId, lines] of grouped.entries()) {
    const poLines: PurchaseOrderLine[] = lines.map((line) => {
      const unitCost =
        line?.unitCostOrdered && line?.unitRatio
          ? line.unitCostOrdered * line.unitRatio
          : undefined

      const orderedQty = line.quantityOrdered ?? 0
      const quantity = creditRequest ? -Math.abs(orderedQty) : orderedQty

      return new PurchaseOrderLine({
        itemId: line.itemId,
        itemCode: line.itemCode,
        unitRatio: line.unitRatio,
        unitCost: unitCost,
        quantityOrdered: quantity,
        quantityReceived: quantity, // The quantity received is initially the same as ordered
        unitCostOrdered: line.unitCostOrdered,
        brandId: line.brandId,
        unitId: line.unitId,
        taxPercent: line.taxPercent,
      })
    })

    const estimatedTotal = poLines.reduce((acc, line) => {
      const cost = line.unitCost ?? 0
      const qty = line.quantityOrdered ?? 0
      return acc + cost * qty
    }, 0)

    const estimatedTotalTax = poLines.reduce((acc, line) => {
      const cost = line.unitCost ?? 0
      const qty = line.quantityOrdered ?? 0
      const taxRate = line.taxPercent ?? 0
      return acc + cost * qty * taxRate
    }, 0)

    // Apply negative values for credit requests
    const finalEstimatedTotal = creditRequest
      ? -Math.abs(estimatedTotal)
      : estimatedTotal
    const finalEstimatedTotalTax = creditRequest
      ? -Math.abs(estimatedTotalTax)
      : estimatedTotalTax

    const po = new PurchaseOrder({
      supplierId,
      lines: poLines,
      creditRequest,
      orderedBy,
      createdAt: dayjs(),
      isReceived: false,
      status: PurchaseOrderStatus.Outstanding,
      subtotal: finalEstimatedTotal,
      tax: finalEstimatedTotalTax,
      total: finalEstimatedTotal + finalEstimatedTotalTax,
      notes: notes,
    })

    purchaseOrders.push(po)
  }

  return purchaseOrders
}

export function convertLinesToOrderTemplate(
  uiLines: PurchaseOrderUiLine[],
  templateName: string,
  suppliers: Record<string, ISupplier>,
  items: Record<string, IItem>,
  units: Record<string, IUnit>,
  creditRequest: boolean = false
): OrderTemplate {
  const orderTemplateLines = uiLines.map((line) => {
    const quantity = creditRequest
      ? -Math.abs(line.quantityOrdered ?? 0)
      : line.quantityOrdered ?? 0

    return new OrderTemplateLine({
      stockItemId: line.itemId,
      stockItemName: (line.itemId && items[line.itemId].name) || '',
      supplierId: line.supplierId,
      supplierName: (line.supplierId && suppliers[line.supplierId].name) || '',
      brandId: line.brandId,
      unitId: line.unitId,
      unitName: (line.unitId && units[line.unitId].name) || '',
      quantity: quantity,
      unitCost: line.unitCost,
      stockCode: line.itemCode,
    })
  })

  const orderTemplate = new OrderTemplate({
    name: templateName,
    createdAt: dayjs(),
    lines: orderTemplateLines,
    creditRequest: creditRequest,
  })

  return orderTemplate
}
